<script setup>
import XlButton from '../xl/XlButton.vue';
import XlTooltip from '../xl/XlTooltip.vue';

/**
 * The prototype picks between a handful of known values with buttons, never a
 * dropdown: equal buttons side by side for an either/or, a wrapping row of tag
 * chips for a list of named things. A button that toggles alongside its
 * neighbours, and every chip that is on, carries a tick; an either/or does not.
 *
 * A dropdown hides its options until opened; these are all short, closed sets
 * where seeing the choices IS the explanation of the field.
 */
const props = defineProps({
  label: { type: String, default: '' },
  hint: { type: String, default: '' },
  options: { type: Array, required: true },
  modelValue: { type: [String, Number, Array], default: null },
  multiple: { type: Boolean, default: false },
  // Two or three mutually exclusive values share the row evenly; chips take
  // their natural width and wrap.
  equal: { type: Boolean, default: false },
  size: { type: String, default: 'md', validator: (v) => ['md', 'lg'].includes(v) },
  tip: { type: String, default: '' },
  testid: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const isOn = (value) => (props.multiple
  ? (props.modelValue || []).includes(value)
  : props.modelValue === value);

const choose = (value) => {
  if (!props.multiple) {
    emit('update:modelValue', value);
    return;
  }

  const current = props.modelValue || [];

  emit('update:modelValue', current.includes(value)
    ? current.filter((entry) => entry !== value)
    : [...current, value]);
};
</script>

<template>
  <div class="choice">
    <span v-if="props.label" class="choice__label">
      <XlTooltip v-if="props.tip" :text="props.tip" underline>{{ props.label }}</XlTooltip>
      <template v-else>{{ props.label }}</template><span v-if="props.hint" class="choice__hint"> — {{ props.hint }}</span>
    </span>

    <div class="choice__row" :class="{ 'choice__row--equal': props.equal }" role="group" :aria-label="props.label || undefined">
      <template v-if="props.equal">
        <XlButton
          v-for="option in props.options"
          :key="option.value"
          size="sm"
          :variant="isOn(option.value) ? 'filled' : 'ghost'"
          :data-testid="props.testid ? `${props.testid}-${option.value}` : undefined"
          :aria-pressed="isOn(option.value) ? 'true' : 'false'"
          class="choice__btn"
          @click="choose(option.value)"
        >
          <template v-if="props.multiple && isOn(option.value)" #icon>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="m5.8 12.3 4.2 4.2L18.2 7.8" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </template>
          {{ option.label }}
        </XlButton>
      </template>

      <template v-else>
        <button
          v-for="option in props.options"
          :key="option.value"
          type="button"
          class="choice__chip"
          :class="{ 'is-on': isOn(option.value), 'choice__chip--lg': props.size === 'lg' }"
          :data-testid="props.testid ? `${props.testid}-${option.value}` : undefined"
          :aria-pressed="isOn(option.value) ? 'true' : 'false'"
          @click="choose(option.value)"
        >
          <svg v-if="isOn(option.value)" :width="props.size === 'lg' ? 13 : 12" :height="props.size === 'lg' ? 13 : 12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="m5.8 12.3 4.2 4.2L18.2 7.8" stroke="currentColor" :stroke-width="props.size === 'lg' ? 1.9 : 2.1" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          {{ option.label }}
        </button>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.choice {
  display: flex;
  flex-direction: column;
  gap: 6px;

  &__label {
    font-size: 12.5px;
    font-weight: 500;
    color: var(--xl-color-secondary-text);
  }

  &__hint {
    font-weight: 400;
    color: var(--xl-color-outline-variant);
  }

  &__row {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__row--equal .choice__btn { flex: 1; }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    flex: 0 0 auto;
    height: 34px;
    padding: 0 14px;
    border: 0;
    border-radius: 7px;
    background: var(--xl-color-border-subtle);
    color: var(--xl-color-secondary-text);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: normal;
    white-space: nowrap;
    cursor: pointer;

    &.is-on {
      background: var(--xl-color-primary-container);
      color: var(--xl-color-primary);
    }

    &:focus-visible {
      outline: 2px solid var(--xl-color-primary);
      outline-offset: 2px;
    }
  }

  &__chip--lg {
    height: 36px;
    padding: 0 16px;
    font-size: 14px;
  }
}
</style>
